import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { TIMEZONES } from "@/components/TimeAndTzPicker";
import TimezoneSelect, { ITimezone } from "react-timezone-select";
import { automationQueries, setGeneralConfig } from "@/api/automations.ts";
import { toast } from "@/hooks/use-toast.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LoadingButton } from "@/components/LoadingButton.tsx";

/**
 * Local wizard shape:
 *   - `operation_window.start` / `operation_window.end`
 *   - `timezone`
 *   - `max_posts_per_day`
 *   - `auto_replenish.enabled/threshold/amount`
 */
interface SetupConfig {
  operation_window: {
    start: number;
    end: number;
  };
  timezone: string;
  max_posts_per_day: number;
  auto_replenish: {
    enabled: boolean;
    threshold: number;
    amount: number;
  };
}

/**
 * Final shape your backend expects (matching GeneralConfigCreate in TypeScript)
 */
interface GeneralConfigCreateShape {
  operation_window_start: number;
  operation_window_end: number;
  max_posts_per_day: number;
  auto_replenish_credits: boolean;
  auto_replenish_threshold: number;
  auto_replenish_amount: number;
  timezone: string;
  metadata?: Record<string, any>;
}

/**
 * Multi-step wizard for first-time config:
 * 1) Operating Hours & Timezone
 * 2) Post Limits
 * 3) Credit Management
 */
export function SetupCapabilitiesConfig({
  personaId,
  onFinish,
}: {
  personaId: string;
  onFinish: () => void;
}) {
  const queryClient = useQueryClient();
  const saveConfigMutation = useMutation({
    mutationFn: (config: GeneralConfigCreateShape) =>
      setGeneralConfig(personaId, config),
    onSuccess: async () => {
      await queryClient.invalidateQueries(
        automationQueries.generalConfig(personaId),
      );
      toast({
        title: "Setup Complete",
        description: "Your automation settings have been initialized.",
      });
      onFinish();
    },
    onError: () => {
      toast({
        title: "Setup Failed",
        description: "An error occurred during initial setup.",
        variant: "destructive",
      });
    },
  });

  const [step, setStep] = useState(1);

  // Our local wizard state
  const [config, setConfig] = useState<SetupConfig>({
    operation_window: { start: 9, end: 17 },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    max_posts_per_day: 24,
    auto_replenish: { enabled: false, threshold: 100, amount: 1000 },
  });

  const defaultTimeZone = "Europe/London";
  let initialTz = config.timezone;

  const formattedTz = Object.keys(TIMEZONES).find(
    (tz) => tz.toLowerCase() === initialTz.toLowerCase(),
  );

  if (!formattedTz) {
    initialTz = defaultTimeZone;
  }

  const [timeZone, setTimeZone] = useState<ITimezone | string>(initialTz);

  /**
   * Final: build the `GeneralConfigCreateShape` object
   * to match your backend's "setGeneralConfig" structure.
   */
  const handleCompleteSetup = async () => {
    saveConfigMutation.mutate({
      operation_window_start: config.operation_window.start,
      operation_window_end: config.operation_window.end,
      max_posts_per_day: config.max_posts_per_day,
      auto_replenish_credits: config.auto_replenish.enabled,
      auto_replenish_threshold: config.auto_replenish.threshold,
      auto_replenish_amount: config.auto_replenish.amount,
      timezone: config.timezone,
      metadata: {},
    });
  };

  /** Wizard steps */
  const steps = [
    {
      title: "Operating Hours + Timezone",
      description: "Set your active hours and timezone for automations",
      content: (
        <div className="space-y-4">
          {/* Operating Hours */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Select
                value={config.operation_window.start.toString()}
                onValueChange={(value) =>
                  setConfig((prev) => ({
                    ...prev,
                    operation_window: {
                      ...prev.operation_window,
                      start: parseInt(value),
                    },
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select start time" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {i.toString().padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>End Time</Label>
              <Select
                value={config.operation_window.end.toString()}
                onValueChange={(value) =>
                  setConfig((prev) => ({
                    ...prev,
                    operation_window: {
                      ...prev.operation_window,
                      end: parseInt(value),
                    },
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select end time" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {i.toString().padStart(2, "0")}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label>Timezone</Label>
            <TimezoneSelect
              value={timeZone}
              onChange={(val) => setTimeZone(val.value)}
              timezones={TIMEZONES}
              className="timezone-select"
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: "hsl(var(--background))",
                  borderColor: "hsl(var(--border))",
                  borderRadius: "var(--radius)",
                  height: "40px",
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: "hsl(var(--background))",
                  borderColor: "hsl(var(--border))",
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isFocused
                    ? "hsl(var(--accent))"
                    : "hsl(var(--background))",
                  color: state.isFocused
                    ? "hsl(var(--accent-foreground))"
                    : "hsl(var(--foreground))",
                  cursor: "pointer",
                }),
                singleValue: (baseStyles) => ({
                  ...baseStyles,
                  color: "hsl(var(--foreground))",
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                  color: "hsl(var(--foreground))",
                }),
              }}
            />
          </div>

          <Alert>
            <AlertDescription>
              Automations will only run between these hours in the chosen
              timezone to maintain controlled behavior.
            </AlertDescription>
          </Alert>
        </div>
      ),
    },
    {
      title: "Post Limits",
      description: "Configure your posting frequency",
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Maximum Posts per Day</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={config.max_posts_per_day}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  max_posts_per_day: parseInt(e.target.value) || 1,
                }))
              }
            />
          </div>
          <Alert>
            <AlertDescription>
              This limit helps maintain a balanced posting schedule across all
              automations.
            </AlertDescription>
          </Alert>
        </div>
      ),
    },
    {
      title: "Credit Management",
      description: "Set up automatic credit replenishment",
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label>Auto Replenish Credits</Label>
              <p className="text-sm text-muted-foreground">
                Automatically add credits when balance is low
              </p>
            </div>
            <Switch
              checked={config.auto_replenish.enabled}
              onCheckedChange={(checked) =>
                setConfig((prev) => ({
                  ...prev,
                  auto_replenish: {
                    ...prev.auto_replenish,
                    enabled: checked,
                  },
                }))
              }
            />
          </div>

          {config.auto_replenish.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Threshold</Label>
                <Input
                  type="number"
                  min={1}
                  value={config.auto_replenish.threshold}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      auto_replenish: {
                        ...prev.auto_replenish,
                        threshold: parseInt(e.target.value) || 1,
                      },
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  min={1}
                  value={config.auto_replenish.amount}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      auto_replenish: {
                        ...prev.auto_replenish,
                        amount: parseInt(e.target.value) || 1,
                      },
                    }))
                  }
                />
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  const currentStep = steps[step - 1];

  const handleNext = () => {
    if (step < steps.length) {
      setStep(step + 1);
    } else {
      // Final step => build "GeneralConfigCreateShape" + call onSubmit
      handleCompleteSetup();
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex gap-2 mb-6">
        {steps.map((_, i) => (
          <React.Fragment key={i}>
            <div
              className={cn(
                "h-2 rounded-full flex-1 transition-colors",
                step > i ? "bg-primary" : "bg-primary/20",
              )}
            />
            {i < steps.length - 1 && <div className="w-2" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step content */}
      <div className="space-y-4">
        <div className="space-y-1">
          <h3 className="text-lg font-medium">{currentStep.title}</h3>
          <p className="text-sm text-muted-foreground">
            {currentStep.description}
          </p>
        </div>
        <Separator className="my-4" />

        {currentStep.content}
      </div>

      {/* Navigation controls */}
      <div className="flex justify-between pt-4">
        <Button
          variant="ghost"
          onClick={() => setStep((prev) => prev - 1)}
          disabled={step === 1 || saveConfigMutation.isPending}
        >
          Back
        </Button>
        <LoadingButton
          onClick={handleNext}
          loading={saveConfigMutation.isPending}
        >
          {step < steps.length ? "Continue" : "Complete Setup"}
        </LoadingButton>
      </div>
    </div>
  );
}
